package fr.frux.web

import fr.frux.resources.ResourceBytesSource
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.fetch.Response
import kotlin.js.Promise

/**
 * Lit une ressource Compose par un `fetch` direct, sans passer par `CacheStorage` (#673).
 *
 * Repli du lecteur par défaut quand le navigateur refuse son cache (navigation privée, données
 * de site bloquées). [uriOf] est le `getUri` du lecteur par défaut : les chemins restent ceux de
 * `resourcePathMapping`, et le cache HTTP du navigateur fait le reste.
 *
 * Une lecture partielle relit la ressource entière et la découpe : c'est un chemin de secours, la
 * simplicité prime sur l'économie d'un en-tête `Range` que tous les hébergeurs n'honorent pas.
 */
class FetchResourceBytesSource(
    private val uriOf: (String) -> String,
) : ResourceBytesSource {
    override suspend fun read(path: String): ByteArray {
        // Sans options : le RequestInit() de Kotlin/JS pose chaque champ à null, et Chrome refuse
        // un `cache: null` (TypeError avant même la requête).
        val response =
            window
                .asDynamic()
                .fetch(uriOf(path))
                .unsafeCast<Promise<Response>>()
                .await()
        check(response.ok) { "Resource fetch failed for $path: HTTP ${response.status}" }
        val buffer: ArrayBuffer = response.arrayBuffer().await()
        return Int8Array(buffer).unsafeCast<ByteArray>()
    }

    override suspend fun readPart(
        path: String,
        offset: Long,
        size: Long,
    ): ByteArray {
        val whole = read(path)
        val from = offset.toInt()
        val to = minOf(whole.size, from + size.toInt())
        return whole.copyOfRange(from, to)
    }

    override fun getUri(path: String): String = uriOf(path)
}
